import { NextPage } from "next";
import { useLocale, useMessages } from "next-intl";
import { StatusCodes } from "http-status-codes";
import { INextPageProps } from "../../../app";
import BlogContentApi from "@carfax/contentful/blog-content-api";
import { languageConfig } from "i18n/language-config";
import { loadServerTranslations } from "../../../i18n/messages";
import Error from "pages/_error";
import { Article } from "domain/BlogAndNews/Article";
import { CategoryPage } from "domain/BlogAndNews/CategoryPage";
import { resolveBlogSlugPage, BlogSlugPage } from "domain/BlogAndNews/utils/resolveBlogSlugPage";

type Props = INextPageProps & BlogSlugPage;

const BlogSlug: NextPage<Props> = (props) => {
  const locale = useLocale();
  const messages = useMessages();

  if (props.kind === "notFound") {
    return <Error statusCode={StatusCodes.NOT_FOUND} locale={locale} messages={messages} />;
  }

  if (props.kind === "category") {
    const { category, blogPosts, stickyVinSearch, translatedSlugs } = props;

    return (
      <CategoryPage
        category={category}
        blogPosts={blogPosts}
        stickyVinSearch={stickyVinSearch}
        translatedSlugs={translatedSlugs}
      />
    );
  }

  const { blogPost, categories, blogPosts, stickyVinSearch, translatedSlugs } = props;

  return (
    <Article
      postExists
      blogPost={blogPost}
      categories={categories}
      blogPosts={blogPosts}
      stickyVinSearch={stickyVinSearch}
      translatedSlugs={translatedSlugs}
    />
  );
};

export const getStaticProps = async ({ params: { slug = null, lang = null } = {} }) => {
  if (!languageConfig.isLanguageAllowedInPath(lang)) {
    return {
      notFound: true,
    };
  }

  const locale = languageConfig.mapLanguageToLocale(lang);
  const page = await resolveBlogSlugPage(locale, slug);

  if (page.kind === "notFound") {
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  return {
    props: {
      ...page,
      locale,
      messages: await loadServerTranslations(locale, ["common", "page-index", "page-error", "page-blog"]),
    },
    revalidate: 60,
  };
};

export async function getStaticPaths() {
  const langs = languageConfig.getLanguages();
  const paths = [];

  const contentful = BlogContentApi.getInstance();

  const promises = langs
    .filter((lang) => lang != "en")
    .map(async (lang) => {
      const locale = languageConfig.mapLanguageToLocale(lang);
      const [blogPosts, categories] = await Promise.all([
        contentful.getBlogPosts(locale),
        contentful.getCategories(locale),
      ]);

      const slugs = new Set<string>([
        ...blogPosts.entries.map((post) => post.slug),
        ...categories.entries.map((category) => category.fields.slug),
      ]);

      slugs.forEach((slug) => {
        paths.push({
          params: { lang, slug },
        });
      });
    });
  await Promise.all(promises);

  return { paths, fallback: "blocking" };
}

export default BlogSlug;
